import {useEffect, useMemo, useState} from "react";
import {AlertCircle, CheckCircle2, Link2} from "lucide-react";
import {getCustomerPollEntryPoints} from "../../../api/message/automate-messages.api";

/**
 * 설문조사 자동화(`CUSTOMER_POLL`) 카드 — **설문 링크(진입점) 선택**. (2026-10-06)
 *
 * 자동화 메시지에는 설문 프로젝트를 고르는 칸이 없다. 버튼 링크가 진입점 짧은링크(`/sh/{uid 8자}`)이고,
 * 서버가 그 진입점을 포함한 **진행중 프로젝트**를 찾아 **설문이 켜져 있으면** 로그인 뒤 설문을 띄운다.
 * ⇒ "어느 설문으로 가느냐" = "버튼에 어느 진입점 링크를 넣느냐" 이다.
 *
 * 종전에는 링크를 바꿀 화면이 없어 DB 를 직접 고쳤고, 다른 채널 진입점 링크가 들어가 고객이
 * '종료된 이벤트입니다' 를 본 사고가 있었다(2026-07-27). 그래서 링크를 타이핑시키지 않고
 * **이 채널의 진입점 목록에서 고르게** 하고, 고른 진입점이 실제로 설문까지 닿는지 같이 보여준다.
 *
 * 선택하면 버튼 1의 링크를 바꾼다. 버튼 1 **PC 링크**·이미지 클릭 링크(`image.imgLink`)도 **값이 있으면** 같은 링크로 맞춘다 —
 * 그대로 두면 PC·이미지로 누른 고객만 예전 링크로 간다.
 *
 * 🔴 PC 링크를 빠뜨리면 저장이 막힌다(2026-10-06 로컬 신고). 다른 채널 메시지를 복사해 만든 행은 PC 링크에도 그 채널
 *    진입점이 들어 있는데, 이 카드에는 PC 링크 입력란이 없어 고칠 방법이 없고 서버 `MessageLinkOwnershipGuard` 는
 *    PC 링크까지 검사해 400("메시지 버튼[1] PC 링크: 다른 고객사의 진입점 링크입니다")을 낸다.
 *
 * @param messageData 카드가 편집 중인 메시지
 * @param onChange (nextMessageData) => void
 * @param onStatusChange (status | null) => void — 카드가 켜기·저장 때 한 번 더 물을지 정하는 데 쓴다.
 *        목록을 못 불러왔으면 null(판정 불가 → 묻지 않는다).
 */

const SHORT_LINK_TOKEN = /\/sh\/([0-9a-zA-Z-]+)/;

/** 짧은링크의 uid 토큰(소문자). 짧은링크가 아니면 null. 호스트는 비교하지 않는다(omopl.com·event.omopl.com 등 혼재). */
export const shortLinkToken = (url) => {
    const match = typeof url === "string" ? url.match(SHORT_LINK_TOKEN) : null;
    return match ? match[1].toLowerCase() : null;
};

/** 현재 버튼 링크가 가리키는 후보. 없으면 null. */
export const findSelectedCandidate = (candidates, url) => {
    const token = shortLinkToken(url);
    if (!token) return null;
    return candidates.find(candidate => shortLinkToken(candidate.linkUrl) === token) ?? null;
};

/** 버튼 1(모바일·PC)·이미지 링크를 새 링크로 바꾼 메시지. 원본은 건드리지 않는다. */
export const applyPollLink = (messageData, linkUrl) => {
    const buttons = [...(messageData.buttons ?? [])];
    const first = buttons[0] ?? {};
    buttons[0] = {...first, type: first.type || "WL", name: first.name || "설문 참여하기", urlMobile: linkUrl};
    if (first.urlPc) {
        buttons[0].urlPc = linkUrl;
    }

    const next = {...messageData, buttons};
    if (messageData.image?.imgLink) {
        next.image = {...messageData.image, imgLink: linkUrl};
    }
    return next;
};

/** 후보 상태 → 화면 문구. `ok` 가 아니면 저장(켜기) 때 한 번 더 묻는다. */
export const pollLinkStatus = (candidate, url) => {
    if (!url) return {ok: false, tone: "warn", text: "설문 링크가 비어 있습니다. 진입점을 선택해 주세요."};
    if (!candidate) {
        return {ok: false, tone: "warn", text: "이 채널의 진입점 링크가 아닙니다. 설문 연결 여부를 확인할 수 없습니다."};
    }
    if (candidate.projectId == null) {
        return {ok: false, tone: "error", text: "이 진입점에 연결된 진행중 프로젝트가 없습니다. 고객이 링크를 누르면 '종료된 이벤트입니다' 가 표시됩니다."};
    }
    if (!candidate.surveyUsed) {
        return {ok: false, tone: "warn", text: `'${candidate.projectName}' 프로젝트에 설문조사가 켜져 있지 않습니다. 설문 없이 이벤트로 바로 이동합니다.`};
    }
    return {ok: true, tone: "ok", text: `'${candidate.projectName}' 프로젝트의 설문조사로 연결됩니다.`};
};

const TONE_CLASS = {
    ok: "text-green-700 bg-green-50 border-green-100",
    warn: "text-amber-700 bg-amber-50 border-amber-100",
    error: "text-red-600 bg-red-50 border-red-100",
};

const CustomerPollLinkSetting = ({messageData, onChange, onStatusChange}) => {
    const [candidates, setCandidates] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let alive = true;
        getCustomerPollEntryPoints().then(body => {
            if (!alive) return;
            // fetch.js 는 실패를 reject 하지 않고 빈 값을 돌려줄 수 있다 — 모양으로 판정한다.
            if (Array.isArray(body?.data)) setCandidates(body.data);
            else setFailed(true);
        });
        return () => { alive = false; };
    }, []);

    const url = messageData.buttons?.[0]?.urlMobile ?? "";
    const selected = useMemo(() => findSelectedCandidate(candidates ?? [], url), [candidates, url]);
    const status = useMemo(() => (candidates ? pollLinkStatus(selected, url) : null), [candidates, selected, url]);

    useEffect(() => {
        onStatusChange?.(status);
    }, [status, onStatusChange]);

    return (
        <div className="space-y-3">
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="w-1 h-4 bg-black rounded-full"></span>
                설문 링크 (진입점)
            </h4>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-3">
                <p className="text-xs text-gray-500 leading-relaxed">
                    버튼을 누르면 선택한 진입점으로 들어가, 그 진입점이 연결된 <b>진행중 프로젝트의 설문조사</b>가 표시됩니다.
                    설문 내용은 프로젝트 상세의 '이벤트 참여 선행 조건 → 설문조사' 에서 설정합니다.
                </p>
                {failed && <div className="text-xs text-red-600">진입점 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.</div>}
                {!failed && !candidates && <div className="text-xs text-gray-400">진입점 목록을 불러오는 중…</div>}
                {candidates && (
                    <select
                        value={selected ? String(selected.id) : ""}
                        onChange={(e) => {
                            const candidate = candidates.find(c => String(c.id) === e.target.value);
                            if (candidate) onChange(applyPollLink(messageData, candidate.linkUrl));
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                        <option value="" disabled>{url ? "직접 입력된 링크 (목록에 없음)" : "진입점을 선택하세요"}</option>
                        {candidates.map(candidate => (
                            <option key={candidate.id} value={String(candidate.id)}>
                                {candidate.name}
                                {candidate.surveyReady
                                    ? ` — 설문: ${candidate.projectName}`
                                    : candidate.projectId != null ? ` — ${candidate.projectName} (설문 꺼짐)` : " — 진행중 프로젝트 없음"}
                            </option>
                        ))}
                    </select>
                )}
                {url && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 break-all">
                        <Link2 size={12} className="shrink-0" />
                        <span>{url}</span>
                    </div>
                )}
                {status && (
                    <div className={`flex items-start gap-1.5 text-xs border rounded p-2 ${TONE_CLASS[status.tone]}`}>
                        {status.ok ? <CheckCircle2 size={14} className="shrink-0 mt-0.5" /> : <AlertCircle size={14} className="shrink-0 mt-0.5" />}
                        <span>{status.text}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerPollLinkSetting;
